import React, { useContext, useEffect, useState } from "react";
import AdminNavbar from "./AdminNavbar";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Footer from "components/framework/footer/Footer";
import Sidebar from "components/framework/sidebar/Sidebar";
import { BodyClassUtil } from "services/util/BodyClassUtil";
import { hasAnyPermissions } from "services/authorization/AuthorizationService";
import { AppContext } from "services/appContext/AppContext";
import ClientEngagementModal from "../modals/ClientEngagementModal";
import { UserApi } from "services/apis/UserApi";
import { handleError } from "services/util/ApiUtil";
import { EnvironmentUtil } from "services/util/EnvironmentUtil";
import { FeedBackInfoDto } from "services/apis/types/user/FeedBackInfoDto";

type Props = {
  children: React.ReactNode;
};

const CLIENT_ENGAGEMENT_STORAGE_KEY = "clientEngagementFormCompleted";

export default function AdminLayout(props: Props) {
  const [sidenavOpen, setSidenavOpen] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isManualToggle, setIsManualToggle] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showLoadingIndicator, setShowLoadingIndicator] = useState(false);
  const [feedBackInfoData, setFeedBackInfoData] = useState<FeedBackInfoDto>();
  const { appContext, setAppContext } = useContext(AppContext);
  const theme = appContext.theme;
  const isMixNetworkUrl = EnvironmentUtil.isMixNetwork;

  const toggleSidenav = () => {
    setIsManualToggle(!isManualToggle);
    if (BodyClassUtil.hasClass("g-sidenav-pinned")) {
      BodyClassUtil.removeClass("g-sidenav-pinned");
      BodyClassUtil.addClass("g-sidenav-hidden");
    } else {
      BodyClassUtil.addClass("g-sidenav-pinned");
      BodyClassUtil.removeClass("g-sidenav-hidden");
    }

    setSidenavOpen(!sidenavOpen);
  };

  // Check if user has completed the form
  useEffect(() => {
    const showFeedbackPopupModal = appContext.localStorageInfo.user?.showFeedbackPopUp;
    if (showFeedbackPopupModal) {
      GetFeedBackPopupInfo();
      setShowModal(true);
    }
  },[appContext.localStorageInfo.user?.showFeedbackPopUp]);

  // Handle when user completes the form
  const handleFormCompleted = async () => {
    UserApi.clientEngagement(true)
    .then(() => {
      // Ensure user exists before updating
      if (appContext.localStorageInfo.user) {
        setAppContext({
          ...appContext,
          localStorageInfo: {
            ...appContext.localStorageInfo, 
            user: {
              ...appContext.localStorageInfo.user,
              showFeedbackPopUp: false
            }
          }
        });
      }
      setShowLoadingIndicator(false);
      // Close the modal
      setShowModal(false);
    })
    .catch((error) => {
      handleError(error);
      setShowLoadingIndicator(false);
      setShowModal(false);
    });
  };

  // Handle when user clicks "Remind Me Later"
  const handleRemindLater = async () => {
    setShowLoadingIndicator(true)
    UserApi.clientEngagement(false)
    .then(() => {
      setShowLoadingIndicator(false);
      if (appContext.localStorageInfo.user) {
        setAppContext({
          ...appContext,
          localStorageInfo: {
            ...appContext.localStorageInfo, 
            user: {
              ...appContext.localStorageInfo.user,
              showFeedbackPopUp: false
            }
          }
        });
      }
      setShowModal(false);
    })
    .catch((error) => {
      setShowLoadingIndicator(false);
      handleError(error);
      setShowModal(false);
    })
  };

  const GetFeedBackPopupInfo = async () => {
    await UserApi.feedbackPopupInfo()
      .then((response: FeedBackInfoDto) => {
        setFeedBackInfoData(response)
      })
      .catch((error) => {
        handleError(error);
      })
  }

  useEffect(() => {
    const handleResize = () => {
      if (!isManualToggle) {
        if (window.innerWidth > 1500) {
          BodyClassUtil.addClass("g-sidenav-pinned");
          BodyClassUtil.removeClass("g-sidenav-hidden");
        } else {
          BodyClassUtil.removeClass("g-sidenav-pinned");
          BodyClassUtil.addClass("g-sidenav-hidden");
        }
      }
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isManualToggle]);

  return (
    <>
      <ToastContainer />
      {hasAnyPermissions() && <Sidebar windowWidth={windowWidth} sidenavOpen={sidenavOpen} toggleSidenav={toggleSidenav} />}
      <div className={theme === "light"? "main-content bg-lcolor" : "main-content bg-dark"}>
        <AdminNavbar sidenavOpen={sidenavOpen} toggleSidenav={toggleSidenav} />
        <div className="mb-2">{props.children}</div>
        <Footer />
      </div>
      {(showModal && !isMixNetworkUrl && feedBackInfoData ) && 
        <ClientEngagementModal 
          feedBackInfoData={feedBackInfoData}
          closeModal={handleRemindLater}
          onCompleteForm={handleFormCompleted}
          showLoadingIndicator={showLoadingIndicator}
        />
      }
    </>
  );
}